import type { PrescriptionItem } from '@/types';
import { Box } from '@mui/material';
import { Prescription } from './Prescription';
import { PrescriptionEmptyState } from './PrescriptionEmptyState';
import type { CancelProductCallback, CancelProductPredicate } from './PrescriptionMedicationsTable.types';

interface PrescriptionListProps {
  data: PrescriptionItem[];
  searchTerm?: string;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  onCancelProduct?: CancelProductCallback;
  isProductCancellable?: CancelProductPredicate;
  hasCancelPermissions?: boolean;
}

export const PrescriptionList = ({
  data,
  searchTerm,
  isLoading,
  isSuccess,
  isError,
  onCancelProduct,
  isProductCancellable,
  hasCancelPermissions
}: PrescriptionListProps) => {
  const noResultsFound = !data?.length && isSuccess;

  if (isLoading) {
    return (
      <Box gap={6} display="flex" flexDirection="column">
        {[...Array(3)].map((_, index) => (
          <Prescription key={`skeleton-${index}`} isLoading />
        ))}
      </Box>
    );
  }

  if (isError) {
    return <PrescriptionEmptyState primaryText="Something went wrong" />;
  }

  if (noResultsFound) {
    return (
      <PrescriptionEmptyState
        primaryText={searchTerm ? <>No results found for &ldquo;{searchTerm}&rdquo;.</> : 'No results found.'}
        secondaryText={searchTerm ? 'Try adjusting your search or check for typos.' : undefined}
      />
    );
  }

  return (
    <Box gap={6} display="flex" flexDirection="column">
      {data.map((prescription) => (
        <Prescription
          key={prescription.id}
          prescription={prescription}
          onCancelProduct={onCancelProduct}
          isProductCancellable={isProductCancellable}
          hasCancelPermissions={hasCancelPermissions}
        />
      ))}
    </Box>
  );
};
